// settings — localStorage キー `mj.settings.v1` の唯一のオーナー (設計書 §5.3 / D10)。
// アプリ全体の表示・音・演出・入力・QoL・検証ツール設定を 1 オブジェクトで永続化する。
// saveClient.ts の流儀に倣い、読み書きはすべて try/catch ガード（SSR / vitest(node) /
// Safari private でも throw しない）。`{ version: 1, ... }` 形式でマイグレーション余地を残す。
//
// 消費者:
//   - App.tsx ……… skipTitle / data-motion / 起動時の sfx.setVolume
//   - SettingsPanel … 全項目の編集 UI（qol.* は対局ドロワー側 = integrate 担当なのでここに UI なし）
//   - SoloMahjong …… integrate トラックが qol.* / display.* / input.* を hydrate（§10.5）
// sound.ts は永続化を持たない (D10)。音量の hydrate は shell/integrate がここから読んで
// `sfx.setVolume(...)` を呼ぶ一方向。
import { useSyncExternalStore } from 'react';

export const SETTINGS_KEY = 'mj.settings.v1';

export type MotionIntensity = 'auto' | 'full' | 'reduced';
export type TileScale = 0.85 | 1 | 1.15;
export type DiscardConfirm = 'single' | 'double';

export interface Settings {
  version: 1;
  audio: {
    /** 0–100（スライダ値）。sfx へは /100 して渡す。 */
    masterVolume: number;
    muted: boolean;
  };
  motion: {
    /** 'auto' = OS の prefers-reduced-motion に従う (D15)。 */
    intensity: MotionIntensity;
  };
  display: {
    /** 回転卓（1〜4人を席順に卓上配置）。対局ドロワーのトグルと同一ソース。 */
    orientedTable: boolean;
    tileScale: TileScale;
    /** アシスト表示（聴牌金枠・打牌バッジ等）。 */
    assist: boolean;
    /** 河のツモ切り牌の色分け。 */
    tsumogiriTint: boolean;
    /** true なら boot 直行で対局画面（自動配牌）。 */
    skipTitle: boolean;
  };
  input: {
    /** 打牌方式 (D18)。既定はポインタ=single / タッチ=double。 */
    discardConfirm: DiscardConfirm;
  };
  qol: {
    /** 鳴き確認スキップ（QoL。ルールの noCalls とは別物）。 */
    skipCalls: boolean;
    autoWin: boolean;
    autoNuki: boolean;
  };
  dev: {
    /** false で対局ドロワーの検証ツール欄を非表示（機能は隠すだけで消さない、§0-5）。 */
    showTools: boolean;
    /** 検証ツールアコーディオンの開閉状態。 */
    toolsOpen: boolean;
  };
}

/** 1 セクション分だけ部分更新できる patch 形（version は patch 不可）。 */
export type SettingsPatch = {
  [K in Exclude<keyof Settings, 'version'>]?: Partial<Settings[K]>;
};

// ---------------------------------------------------------------------------
// defaults
// ---------------------------------------------------------------------------

/** D18: タッチ主体 (pointer: coarse) は誤タップ防止で 2 タップ確認を既定にする。 */
function defaultDiscardConfirm(): DiscardConfirm {
  try {
    return typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches
      ? 'double'
      : 'single';
  } catch {
    return 'single';
  }
}

export function defaultSettings(): Settings {
  return {
    version: 1,
    audio: { masterVolume: 70, muted: false },
    motion: { intensity: 'auto' },
    display: {
      orientedTable: true,
      tileScale: 1,
      assist: true,
      tsumogiriTint: true,
      skipTitle: false,
    },
    input: { discardConfirm: defaultDiscardConfirm() },
    qol: { skipCalls: false, autoWin: false, autoNuki: false },
    dev: { showTools: true, toolsOpen: false },
  };
}

// ---------------------------------------------------------------------------
// sanitize / load / save
// ---------------------------------------------------------------------------

function asBool(v: unknown, fallback: boolean): boolean {
  return typeof v === 'boolean' ? v : fallback;
}
function asVolume(v: unknown, fallback: number): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) return fallback;
  return Math.min(100, Math.max(0, Math.round(v)));
}
function oneOf<T>(v: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(v as T) ? (v as T) : fallback;
}

/**
 * 永続化 JSON を Settings に正規化する。未知キーは捨て、欠けたキーは既定値で埋める
 * （将来キー追加しても古い保存が壊れない）。version 不一致は丸ごと既定値に落とす
 * （version 2 を作るときはここに移行ロジックを足す）。
 */
function sanitize(raw: unknown): Settings {
  const d = defaultSettings();
  if (!raw || typeof raw !== 'object') return d;
  const v = raw as { [k: string]: unknown };
  if (v['version'] !== 1) return d;
  const sec = (k: string): { [k: string]: unknown } => {
    const o = v[k];
    return o && typeof o === 'object' ? (o as { [k: string]: unknown }) : {};
  };
  const audio = sec('audio');
  const motion = sec('motion');
  const display = sec('display');
  const input = sec('input');
  const qol = sec('qol');
  const dev = sec('dev');
  return {
    version: 1,
    audio: {
      masterVolume: asVolume(audio['masterVolume'], d.audio.masterVolume),
      muted: asBool(audio['muted'], d.audio.muted),
    },
    motion: {
      intensity: oneOf<MotionIntensity>(motion['intensity'], ['auto', 'full', 'reduced'], d.motion.intensity),
    },
    display: {
      orientedTable: asBool(display['orientedTable'], d.display.orientedTable),
      tileScale: oneOf<TileScale>(display['tileScale'], [0.85, 1, 1.15], d.display.tileScale),
      assist: asBool(display['assist'], d.display.assist),
      tsumogiriTint: asBool(display['tsumogiriTint'], d.display.tsumogiriTint),
      skipTitle: asBool(display['skipTitle'], d.display.skipTitle),
    },
    input: {
      discardConfirm: oneOf<DiscardConfirm>(input['discardConfirm'], ['single', 'double'], d.input.discardConfirm),
    },
    qol: {
      skipCalls: asBool(qol['skipCalls'], d.qol.skipCalls),
      autoWin: asBool(qol['autoWin'], d.qol.autoWin),
      autoNuki: asBool(qol['autoNuki'], d.qol.autoNuki),
    },
    dev: {
      showTools: asBool(dev['showTools'], d.dev.showTools),
      toolsOpen: asBool(dev['toolsOpen'], d.dev.toolsOpen),
    },
  };
}

/** localStorage から読む（無い/壊れている/使えないときは既定値）。 */
export function loadSettings(): Settings {
  try {
    const s = localStorage.getItem(SETTINGS_KEY);
    if (!s) return defaultSettings();
    return sanitize(JSON.parse(s));
  } catch {
    return defaultSettings();
  }
}

/** localStorage へ書く（失敗は黙殺 — オンメモリ状態が正のまま動き続ける）。 */
export function saveSettings(s: Settings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
  } catch {
    /* private mode 等。揮発でよい */
  }
}

// ---------------------------------------------------------------------------
// in-memory singleton + subscribe / patch
// ---------------------------------------------------------------------------

let current: Settings | null = null;
const listeners = new Set<(s: Settings) => void>();

/** 現在値スナップショット（patch まで同一参照 → useSyncExternalStore 安全）。 */
export function getSettings(): Settings {
  if (!current) current = loadSettings();
  return current;
}

/** 変更通知の購読。解除関数を返す。 */
export function subscribeSettings(fn: (s: Settings) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** セクション単位の部分更新 → 保存 → 通知。新しい Settings を返す。 */
export function patchSettings(patch: SettingsPatch): Settings {
  const cur = getSettings();
  const next: Settings = {
    version: 1,
    audio: { ...cur.audio, ...patch.audio },
    motion: { ...cur.motion, ...patch.motion },
    display: { ...cur.display, ...patch.display },
    input: { ...cur.input, ...patch.input },
    qol: { ...cur.qol, ...patch.qol },
    dev: { ...cur.dev, ...patch.dev },
  };
  current = next;
  saveSettings(next);
  for (const fn of [...listeners]) fn(next);
  return next;
}

// ---------------------------------------------------------------------------
// React hooks
// ---------------------------------------------------------------------------

/** 設定の購読フック。patch のたびに新しい参照で再レンダーされる。 */
export function useSettings(): Settings {
  return useSyncExternalStore(subscribeSettings, getSettings);
}

function osPrefersReduced(): boolean {
  try {
    return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** 実効 reduced (D15): intensity==='reduced'、または 'auto' かつ OS が reduce。 */
export function effectiveReduced(s: Settings = getSettings()): boolean {
  const i = s.motion.intensity;
  return i === 'reduced' || (i === 'auto' && osPrefersReduced());
}

/** 設定変更と OS の prefers-reduced-motion 変化の両方を購読する（モジュールレベルで参照安定）。 */
function subscribeReducedSources(cb: () => void): () => void {
  const unsub = subscribeSettings(cb);
  let mq: MediaQueryList | null = null;
  try {
    mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    mq.addEventListener('change', cb);
  } catch {
    mq = null; // jsdom 旧版 / SSR — OS 側変化は追わない（settings 側だけで十分動く）
  }
  return () => {
    unsub();
    mq?.removeEventListener('change', cb);
  };
}

/**
 * 実効 reduced-motion の React フック (D15)。App ルートはこれで
 * `data-motion="full|reduced"` を付与し、theme.css 側の CSS と二重防御になる。
 */
export function useEffectiveReduced(): boolean {
  return useSyncExternalStore(subscribeReducedSources, () => effectiveReduced());
}
